/**
 * Cross-platform ZIP backup container (see backup-schema/README.md · "Backup
 * container"):
 *
 *     backup.json           — the payload produced by serializeToText (unchanged schema)
 *     attachments/<file>    — flat; <file> is the unique UUID.ext referenced in backup.json
 *
 * Android (java.util.zip) and iOS (ZIPFoundation) write DEFLATE entries, so we
 * do too (fflate defaults to deflate). Missing/absent attachments are non-fatal.
 */
import { zipSync, unzipSync, strToU8, strFromU8 } from "fflate";
import type { CanonicalStore } from "../model.ts";
import { serializeToText, type SerializeOptions } from "./serialize.ts";

export const BACKUP_JSON = "backup.json";
export const ATTACH_PREFIX = "attachments/";

/** ZIP local-file-header magic (`PK\x03\x04`). */
export function isZipBytes(u8: Uint8Array): boolean {
  return u8.length >= 4 && u8[0] === 0x50 && u8[1] === 0x4b && u8[2] === 0x03 && u8[3] === 0x04;
}

/** Every attachment filename referenced anywhere in the store (deduped). */
export function referencedAttachmentNames(store: CanonicalStore): string[] {
  const out = new Set<string>();
  const add = (n?: string | null) => { if (n) out.add(n); };
  const addAll = (a?: string[]) => a?.forEach(add);
  for (const c of store.cars) { addAll(c.photoFilenames); add(c.insuranceDocumentFilename); add(c.roadTaxDocumentFilename); }
  for (const l of store.fuelLogs) { addAll(l.photoFilenames); addAll(l.documentFilenames); }
  for (const s of store.spendings) { addAll(s.photoFilenames); addAll(s.documentFilenames); }
  for (const ty of store.tyres) { addAll(ty.photoFilenames); addAll(ty.documentFilenames); }
  return [...out];
}

/** Build a backup `.zip` Blob: backup.json + attachments/<name>. */
export function buildBackupZip(
  store: CanonicalStore,
  attachments: Map<string, Uint8Array>,
  opts: SerializeOptions = {},
): Blob {
  const files: Record<string, Uint8Array> = {};
  files[BACKUP_JSON] = strToU8(serializeToText(store, opts));
  const seen = new Set<string>();
  for (const [name, bytes] of attachments) {
    if (!name || name.includes("/") || name.includes("..") || seen.has(name)) continue;
    seen.add(name);
    files[ATTACH_PREFIX + name] = bytes;
  }
  const zipped = zipSync(files, { level: 6 });
  return new Blob([zipped as BlobPart], { type: "application/zip" });
}

/** Extract a backup `.zip` → the backup.json text and an attachments map. */
export function readBackupZip(u8: Uint8Array): { json: string; attachments: Map<string, Uint8Array> } {
  const entries = unzipSync(u8);
  const jsonBytes = entries[BACKUP_JSON];
  if (!jsonBytes) throw new Error("Backup archive is missing backup.json.");
  const attachments = new Map<string, Uint8Array>();
  for (const [path, bytes] of Object.entries(entries)) {
    if (!path.startsWith(ATTACH_PREFIX)) continue;
    const name = path.slice(ATTACH_PREFIX.length);
    if (name && !name.includes("/") && !name.includes("..")) attachments.set(name, bytes);
  }
  return { json: strFromU8(jsonBytes), attachments };
}
